import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { MEETING_STATUSES } from '@mkcore/crm/schema';
import { EmptyState, ErrorState, Field, Input, NativeSelect, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr } from '@mkcore/ui';
import { useErrorMessage } from '@/lib/mutation';
import { MeetingStatusBadge, meetingWhen, meetingsQuery } from '@/features/crm/meetings';

const search = z.object({ q: z.string().optional(), status: z.enum(MEETING_STATUSES).optional() });
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_app/crm/meetings/')({ validateSearch: search, component: MeetingsPage });

/** All meetings (CO-0584), newest first; a meeting is added from its lead or organisation. */
function MeetingsPage() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const message = useErrorMessage();
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const q = useQuery(meetingsQuery({ q: s.q || undefined, status: s.status ? [s.status] : undefined }));

  return (
    <>
      <PageHeader eyebrow={t('nav.crm')} title={t('meetings.title')} description={t('meetings.description')} />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('common.search')} className="w-64">
          {(p) => <Input {...p} value={s.q ?? ''} onChange={(e) => set({ q: e.target.value || undefined })} />}
        </Field>
        <Field label={t('meetings.fields.status')} className="w-44">
          {(p) => (
            <NativeSelect {...p} value={s.status ?? ''} onChange={(e) => set({ status: (e.target.value || undefined) as never })}>
              <option value="">{t('common.all')}</option>
              {MEETING_STATUSES.map((st) => (
                <option key={st} value={st}>
                  {t(`meetings.status.${st}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
      </div>
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : !q.data ? (
        <Skeleton className="h-64" />
      ) : !q.data.items.length ? (
        <EmptyState title={t('meetings.none')} description={t('meetings.emptyHint')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('meetings.fields.title')}</Th>
              <Th>{t('meetings.fields.startsAt')}</Th>
              <Th className="hidden md:table-cell">{t('meetings.fields.organisation')}</Th>
              <Th className="hidden lg:table-cell">{t('meetings.participants')}</Th>
              <Th>{t('meetings.fields.status')}</Th>
            </Tr>
          </THead>
          <TBody>
            {q.data.items.map((m) => (
              <Tr key={m.id}>
                <Td>
                  <Link to="/crm/meetings/$id" params={{ id: m.id }} className="font-bold hover:underline">
                    {m.title}
                  </Link>
                </Td>
                <Td className="tabular">{meetingWhen(m)}</Td>
                <Td className="hidden md:table-cell">
                  {m.organisationId ? (
                    <Link to="/org/$id" params={{ id: m.organisationId }} className="hover:underline">
                      {m.organisationName}
                    </Link>
                  ) : (
                    '–'
                  )}
                </Td>
                <Td className="hidden lg:table-cell">{m.participants.map((p) => p.name).join(', ') || '–'}</Td>
                <Td>
                  <MeetingStatusBadge status={m.status} />
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </>
  );
}
